import { useCallback, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useMotionValueEvent } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { MaskLine } from "../Reveal";
import { scrollToId } from "../../lib/api";
import { JHeroScene } from "./JHeroScene";
import { JTipBubble } from "./JTipBubble";
import { JWaterToday } from "./JWaterToday";

const EASE = [0.16, 1, 0.3, 1];

export const JHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const leftX = useTransform(scrollYProgress, [0, 1], ["0%", "-28%"]);
  const rightX = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const nearX = useTransform(sx, (v) => v * -28);
  const nearY = useTransform(sy, (v) => v * -18);
  const farX = useTransform(sx, (v) => v * 10);
  const [pointer, setPointer] = useState(null);
  const [poke, setPoke] = useState(0);
  const [talk, setTalk] = useState(null);
  const offsets = useRef({ toucan: 0, monkey: 0 });
  const onTalk = (animal, rect) => {
    const section = ref.current.getBoundingClientRect();
    setTalk({ animal, rect, section, offset: offsets.current[animal] });
  };
  const onNext = () => setTalk((t) => {
    offsets.current[t.animal] += 1;
    return { ...t, offset: offsets.current[t.animal] };
  });
  const onClose = useCallback(() => setTalk(null), []);
  useMotionValueEvent(scrollYProgress, "change", (v) => { if (v > 0.12) setTalk(null); });
  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
    setPointer({ x: e.clientX, y: e.clientY });
  };

  return (
    <section ref={ref} onMouseMove={onMove} onMouseLeave={() => setPointer(null)} onTouchStart={() => setPoke((p) => p + 1)} data-testid="hero-section" className="relative h-[100svh] min-h-[680px] overflow-hidden bg-[#0E2A1C]">
      <motion.div style={{ y: bgY, x: farX }} className="absolute -inset-x-[3%] top-[56px] -bottom-[6%]">
        <JHeroScene sx={sx} sy={sy} pointer={pointer} poke={poke} onTalk={onTalk} />
      </motion.div>
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_30%_42%_at_50%_52%,rgba(14,42,28,0.78),rgba(14,42,28,0.2)_100%)]" />


      <motion.div style={{ x: leftX }} className="absolute left-0 bottom-0 h-[50%] md:h-[72%] pointer-events-none -translate-x-[70%] md:-translate-x-[38%]">
        <motion.div style={{ x: nearX, y: nearY }} className="h-full">
          <motion.img initial={{ x: "45%", scale: 1.3 }} animate={{ x: "0%", scale: 1 }} transition={{ duration: 1.9, ease: EASE }}
            src="/img/leaves-left.webp" alt="" className="h-full w-auto max-w-none origin-bottom-left drop-shadow-[0_30px_40px_rgba(0,0,0,0.5)]" />
        </motion.div>
      </motion.div>
      <motion.div style={{ x: rightX }} className="absolute right-0 bottom-0 h-[64%] md:h-[52%] lg:h-[50%] pointer-events-none translate-x-[58%] md:translate-x-[40%]">
        <motion.div style={{ x: nearX, y: nearY }} className="h-full">
          <motion.img initial={{ x: "-45%", scale: 1.3 }} animate={{ x: "0%", scale: 1 }} transition={{ duration: 1.9, ease: EASE }}
            src="/img/leaves-right.webp" alt="" className="h-full w-auto max-w-none origin-bottom-right drop-shadow-[0_30px_40px_rgba(0,0,0,0.5)]" />
        </motion.div>
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#0E2A1C]/90 via-[#0E2A1C]/50 to-transparent lg:hidden pointer-events-none" />
      <motion.div style={{ y: textY, opacity: fade }} className="relative z-20 h-full flex flex-col items-center justify-end lg:justify-center text-center px-5 pb-14 lg:pb-0 lg:pt-[22vh] lg:pl-[10vw] pointer-events-none">
        <div className="absolute inset-x-0 top-[80px] md:top-[86px] flex justify-center pointer-events-none">
        <motion.p data-testid="hero-badge" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#1B3A8C]/85 border border-[#F2B441]/50 px-3 py-1 font-mono-d text-[9px] md:text-[10px] tracking-[0.16em] uppercase text-[#F6EEDC]">
          <Sparkles className="w-3 h-3 text-[#F2B441]" /> Biotecnología avanzada optimizada por IA
        </motion.p>
        </div>
        <h1 data-testid="hero-title" className="font-serif-d font-black leading-[0.9] tracking-[-0.02em] text-[clamp(2.4rem,11vw,3.6rem)] lg:text-[clamp(2.4rem,4vw,5.2rem)] [text-shadow:0_8px_40px_rgba(0,0,0,0.55)]">
          <MaskLine delay={0.7}>Nutrición</MaskLine>
          <MaskLine delay={0.85} className="font-label font-normal text-label-gold [text-shadow:none] text-[0.92em] py-[0.04em]">SALVAJE</MaskLine>
          <MaskLine delay={1.0} className="italic font-semibold text-[0.62em]">calculada por IA.</MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 1 }}
          className="mt-4 lg:mt-6 max-w-sm lg:max-w-[21vw] text-[#E3EADF] text-[13px] lg:text-[15px] leading-relaxed [text-shadow:0_2px_16px_rgba(0,0,0,0.8)]">
          Fertilizantes formulados con una potente herramienta de IA que ha estudiado la mayoría de genéticas de cannabis para cubrir sus necesidades nutricionales.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 1 }} className="mt-5 lg:mt-7 flex flex-col sm:flex-row lg:flex-col items-center justify-center gap-3 pointer-events-auto">
          <button data-testid="hero-catalog-cta" onClick={() => scrollToId("catalogo")}
            className="group inline-flex items-center gap-3 rounded-full bg-[#F2B441] text-[#0E2A1C] pl-6 pr-1.5 h-12 text-sm font-bold hover:bg-[#F6EEDC] transition-colors shadow-[0_12px_40px_-10px_rgba(242,180,65,0.7)]">
            Entrar a la selva
            <span className="w-9 h-9 rounded-full bg-[#0E2A1C] text-[#F2B441] grid place-items-center transition-transform duration-500 group-hover:rotate-[-45deg]">
              <ArrowDownRight className="w-4 h-4" />
            </span>
          </button>
          <button data-testid="hero-ai-cta" onClick={() => scrollToId("asistente")}
            className="inline-flex items-center gap-2.5 rounded-full bg-[#0E2A1C]/60 backdrop-blur-md border border-[#F6EEDC]/40 px-6 h-12 text-sm font-semibold hover:border-[#F2B441] hover:text-[#F2B441] transition-colors">
            <Sparkles className="w-4 h-4" /> Plan IA para mi genética
          </button>
        </motion.div>
      </motion.div>
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0E2A1C] to-transparent z-10 pointer-events-none" />
      <JTipBubble talk={talk} onNext={onNext} onClose={onClose} />
      <JWaterToday />
    </section>
  );
};
